import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_CONFIG, getApiUrl } from "../../config/apiconfig";
import "./ForgotPassword.scss";

const PASSWORD_SPECIAL_CHARS = "!@#$%^&*()_+-=[]{}|;':\",./<>?`~";

const hasPasswordSpecialChar = (pwd) =>
  [...pwd].some((ch) => PASSWORD_SPECIAL_CHARS.includes(ch));

const validatePasswordPolicy = (pwd) => {
  if (pwd.length < 6 || pwd.length > 20) {
    return "Password must be between 6 and 20 characters.";
  }
  if (!/[A-Z]/.test(pwd)) {
    return "Password must contain at least one capital letter.";
  }
  if (!/\d/.test(pwd)) {
    return "Password must contain at least one number.";
  }
  if (!hasPasswordSpecialChar(pwd)) {
    return "Password must contain at least one special character.";
  }
  return null;
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newAccessCode, setNewAccessCode] = useState("");
  const [confirmAccessCode, setConfirmAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setShowResetForm(false);

    // Basic validation
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const url = `${getApiUrl("checkEmailId")}?${new URLSearchParams({
        Email: email.trim(),
      })}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        API_CONFIG.timeout,
      );
      const res = await fetch(url, {
        method: "GET",
        headers: API_CONFIG.headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const response = await res.json();
      if (response?.success === 1) {
        setSuccess(true);
      } else {
        setError(
          response?.message ||
            "We could not verify this email. Please check and try again.",
        );
      }
    } catch (err) {
      const msg =
        err?.name === "AbortError"
          ? "Request timeout"
          : err?.message ||
            "Something went wrong. Please try again in a moment.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOTP = async (e) => {
    e?.preventDefault?.();
    setError("");

    const trimmed = otp.trim();
    if (!/^\d{8}$/.test(trimmed)) {
      setError("OTP must be exactly 8 digits.");
      return;
    }
    const otpNum = parseInt(trimmed, 10);
    if (otpNum < 10000000 || otpNum > 99999999) {
      setError("OTP must be an 8-digit number.");
      return;
    }

    setLoading(true);
    try {
      const url = getApiUrl("validateOTP");
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        API_CONFIG.timeout,
      );
      const res = await fetch(url, {
        method: "POST",
        headers: API_CONFIG.headers,
        signal: controller.signal,
        body: JSON.stringify({
          EmailId: email.trim(),
          OTP: otpNum,
        }),
      });
      clearTimeout(timeoutId);

      const text = await res.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        throw new Error("Invalid response from server.");
      }

      if (!res.ok) {
        setError(
          data?.message ||
            `Unable to verify OTP (HTTP ${res.status}). Please try again.`,
        );
        return;
      }

      if (data?.success === 1) {
        setShowResetForm(true);
      } else {
        setError(
          data?.message ||
            "Invalid or expired OTP. Please check and try again.",
        );
      }
    } catch (err) {
      const msg =
        err?.name === "AbortError"
          ? "Request timeout"
          : err?.message ||
            "Something went wrong. Please try again in a moment.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please start the process again to reset your login credentials.");
      return;
    }

    const pwdErr = validatePasswordPolicy(newPassword);
    if (pwdErr) {
      setError(pwdErr);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Confirm Password doesn't match.");
      return;
    }

    const nac = newAccessCode.trim();
    const cac = confirmAccessCode.trim();
    if (!/^\d{6}$/.test(nac) || !/^\d{6}$/.test(cac)) {
      setError("Access code must be exactly 6 digits.");
      return;
    }
    const newAccessCodeNum = parseInt(nac, 10);
    const confirmAccessCodeNum = parseInt(cac, 10);
    if (newAccessCodeNum < 100000 || newAccessCodeNum > 999999) {
      setError("New Access Code must be a 6-digit number.");
      return;
    }
    if (confirmAccessCodeNum < 100000 || confirmAccessCodeNum > 999999) {
      setError("Confirm Access Code must be a 6-digit number.");
      return;
    }
    if (newAccessCodeNum !== confirmAccessCodeNum) {
      setError("Confirm Access Code doesn't match.");
      return;
    }

    setLoading(true);
    try {
      const url = getApiUrl("setNewPassword");
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        API_CONFIG.timeout,
      );
      const res = await fetch(url, {
        method: "POST",
        headers: API_CONFIG.headers,
        signal: controller.signal,
        body: JSON.stringify({
          EmailId: trimmedEmail,
          NewPassword: newPassword,
          ConfirmPassword: confirmPassword,
          NewAccessCode: newAccessCodeNum,
          ConfirmAccessCode: confirmAccessCodeNum,
        }),
      });
      clearTimeout(timeoutId);

      const text = await res.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        throw new Error("Invalid response from server.");
      }

      if (!res.ok) {
        setError(
          data?.message ||
            `Unable to reset password (HTTP ${res.status}). Please try again.`,
        );
        return;
      }

      if (data?.success === 1) {
        setPasswordResetSuccess(true);
        setError("");
      } else {
        setError(
          data?.message ||
            "Could not reset your password. Please try again.",
        );
      }
    } catch (err) {
      const msg =
        err?.name === "AbortError"
          ? "Request timeout"
          : err?.message ||
            "Something went wrong. Please try again in a moment.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-wrapper">
        {/* Header Section */}
        <div className="forgot-password-header">
          <div className="forgot-password-logo">
            <img
              src="/logo.png"
              alt="Logo"
              className="forgot-password-logo-img"
            />
          </div>
          <h1 className="forgot-password-title">Forgot Password</h1>
          <p className="forgot-password-subtitle">
            Enter your email address and we'll send you a link to reset your
            password.
          </p>
        </div>

        {/* Form / OTP / Reset Section */}
        <div className="forgot-password-form-card">
          {passwordResetSuccess ? (
            <div className="forgot-password-form">
              <h2 className="forgot-password-title">Password updated</h2>
              <p className="forgot-password-subtitle">
                Your password and access code have been reset. You can sign in
                with your new credentials.
              </p>
              <div className="forgot-password-submit-wrapper">
                <button
                  type="button"
                  className="forgot-password-btn-primary"
                  onClick={() => navigate("/login")}
                >
                  Go to Login
                </button>
              </div>
            </div>
          ) : showResetForm ? (
            <form
              className="forgot-password-form"
              onSubmit={handleResetPassword}
            >
              <h2 className="forgot-password-title">
                Reset Password and Access Code
              </h2>
              <p className="forgot-password-subtitle">
                Please enter below details to complete verification.
              </p>

              {error && (
                <div
                  className="forgot-password-alert forgot-password-alert-danger"
                  role="alert"
                >
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
                </div>
              )}

              <div className="reset-password-grid">
                <div className="forgot-password-form-group">
                  <label
                    className="forgot-password-form-label"
                    htmlFor="new-password"
                  >
                    New Password
                  </label>
                  <div className="forgot-password-input-wrapper">
                    <i className="fas fa-lock forgot-password-input-icon"></i>
                    <input
                      id="new-password"
                      type="password"
                      className="forgot-password-form-input"
                      placeholder="6–20 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </div>
                  <p className="forgot-password-otp-helper">
                    Use at least 1 capital letter, 1 number, and 1 special
                    character.
                  </p>
                </div>

                <div className="forgot-password-form-group">
                  <label
                    className="forgot-password-form-label"
                    htmlFor="confirm-password"
                  >
                    Confirm Password
                  </label>
                  <div className="forgot-password-input-wrapper">
                    <i className="fas fa-lock forgot-password-input-icon"></i>
                    <input
                      id="confirm-password"
                      type="password"
                      className="forgot-password-form-input"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <div className="forgot-password-form-group">
                  <label
                    className="forgot-password-form-label"
                    htmlFor="new-access-code"
                  >
                    New Access Code
                  </label>
                  <div className="forgot-password-input-wrapper">
                    <i className="fas fa-key forgot-password-input-icon"></i>
                    <input
                      id="new-access-code"
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      className="forgot-password-form-input"
                      placeholder="6 digits"
                      value={newAccessCode}
                      onChange={(e) =>
                        setNewAccessCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                      disabled={loading}
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="forgot-password-form-group">
                  <label
                    className="forgot-password-form-label"
                    htmlFor="confirm-access-code"
                  >
                    Confirm Access Code
                  </label>
                  <div className="forgot-password-input-wrapper">
                    <i className="fas fa-key forgot-password-input-icon"></i>
                    <input
                      id="confirm-access-code"
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      className="forgot-password-form-input"
                      placeholder="6 digits"
                      value={confirmAccessCode}
                      onChange={(e) =>
                        setConfirmAccessCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                      disabled={loading}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              <div className="forgot-password-submit-wrapper">
                <button
                  type="submit"
                  className="forgot-password-btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="forgot-password-spinner"></span>
                      Saving...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>

              <div className="forgot-password-back-link">
                <Link to="/login" className="forgot-password-back-link-text">
                  <i className="fas fa-arrow-left"></i>
                  Back to Login
                </Link>
              </div>
            </form>
          ) : success ? (
            <form className="forgot-password-form" onSubmit={handleCheckOTP}>
              {error && (
                <div
                  className="forgot-password-alert forgot-password-alert-danger"
                  role="alert"
                >
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
                </div>
              )}
              <div className="forgot-password-form-group">
                <label className="forgot-password-form-label" htmlFor="otp">
                  Enter OTP
                </label>
                <div className="forgot-password-input-wrapper">
                  <i className="fas fa-key forgot-password-input-icon"></i>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{8}"
                    maxLength={8}
                    className="forgot-password-form-input"
                    placeholder="8-digit OTP"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))
                    }
                    disabled={loading}
                    autoComplete="one-time-code"
                  />
                </div>
                <p className="forgot-password-otp-helper">
                  We have sent a one-time password to <strong>{email}</strong>
                </p>
              </div>

              <div className="forgot-password-submit-wrapper">
                <button
                  type="submit"
                  className="forgot-password-btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="forgot-password-spinner"></span>
                      Verifying...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>

              <div className="forgot-password-back-link">
                <Link to="/login" className="forgot-password-back-link-text">
                  <i className="fas fa-arrow-left"></i>
                  Back to Login
                </Link>
              </div>
            </form>
          ) : (
            <form className="forgot-password-form" onSubmit={handleSubmit}>
              {/* Error message */}
              {error && (
                <div
                  className="forgot-password-alert forgot-password-alert-danger"
                  role="alert"
                >
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
                </div>
              )}

              {/* Email Field */}
              <div className="forgot-password-form-group">
                <label className="forgot-password-form-label" htmlFor="email">
                  Email Address
                </label>
                <div className="forgot-password-input-wrapper">
                  <i className="fas fa-envelope forgot-password-input-icon"></i>
                  <input
                    id="email"
                    type="email"
                    className="forgot-password-form-input"
                    placeholder="name@clinicaltrial.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="forgot-password-submit-wrapper">
                <button
                  type="submit"
                  className="forgot-password-btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="forgot-password-spinner"></span>
                      Sending...
                    </>
                  ) : (
                    "Continue"
                  )}
                </button>
              </div>

              {/* Back to Login */}
              <div className="forgot-password-back-link">
                <Link to="/login" className="forgot-password-back-link-text">
                  <i className="fas fa-arrow-left"></i>
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="forgot-password-footer">
          <p className="forgot-password-footer-text">
            Remember your password?
            <Link to="/login" className="forgot-password-footer-link">
              {" "}
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
